// Copyright ©️ 2026 eVolve MEP, LLC

import { type TaskType, type WorkOrder, type WorkOrderItem, useEvolvePost } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { TaskPickerModal } from 'modules/Shop/Tasks/TaskTypes/TaskPickerModal';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type Props = {
  opened: boolean;
  onClose: () => void;
  workOrder: WorkOrder;
  workOrderItem: WorkOrderItem;
  onTasksUpdated: () => void;
};

type AddTasksToBOPButtonProps = Pick<Props, 'workOrderItem' | 'onTasksUpdated' | 'onClose'> & {
  selectedTasks: TaskType[];
};

const AddTasksToBOPButton = ({ workOrderItem, onTasksUpdated, onClose, selectedTasks }: AddTasksToBOPButtonProps) => {
  const { apiCall: addTasksToWorkOrderItem, loading: saving } = useEvolvePost('shop/task');

  return (
    <Button
      onClick={async () => {
        await addTasksToWorkOrderItem({
          taskTypeIds: selectedTasks.map((task) => task.taskTypeId),
          workOrderItemIds: [workOrderItem.workOrderItemId],
        });
        notifications.show({
          title: 'Successfully added tasks',
          message: `${selectedTasks.length} task${selectedTasks.length === 1 ? '' : 's'} added to ${workOrderItem.name}.`,
          color: 'green',
        });
        onTasksUpdated();
        onClose();
      }}
      disabled={selectedTasks.length === 0}
      loading={saving}
    >
      Add {selectedTasks.length > 0 ? `${selectedTasks.length} ` : ''}task{selectedTasks.length === 1 ? '' : 's'} to
      item
    </Button>
  );
};

export const AddTasksToBOPModal = ({ workOrder, workOrderItem, opened, onClose, onTasksUpdated }: Props) => (
  <TaskPickerModal
    miniHeader={getDisplayNameWO(workOrder)}
    title={workOrderItem.name}
    subtitle="Add tasks to item"
    opened={opened}
    onClose={onClose}
    renderAction={(selectedTasks) => (
      <AddTasksToBOPButton
        workOrderItem={workOrderItem}
        onTasksUpdated={onTasksUpdated}
        onClose={onClose}
        selectedTasks={selectedTasks}
      />
    )}
  />
);
